/**
 * CheckoutPage starts payment process and therefore it will get data from ListingPage
 * (booking dates, listing data, and all the other data that affects to booking decision).
 * This data is saved to Session Store which only exists while the browsing session exists -
 * e.g. tab is open. (Session Store is not related to session cookies.)
 */
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import reduce from 'lodash/reduce';
import moment from 'moment';

import { TRANSITIONS } from '../../util/transaction.default';

const { UUID, Money } = sdkTypes;

// sessionStorage key under which the CheckoutPage persists its bootstrap data
// (bookingData/bookingDates/listing/transaction). Shared by the container and
// the checkout hooks so it stays a single source of truth.
export const STORAGE_KEY = 'CheckoutPage';

// Validate that given 'obj' has all the keys of defined by validPropTypes parameter
// and values must pass related test-value-format function.
const validateProperties = (obj, validPropTypes) => {
  return reduce(
    Object.entries(validPropTypes),
    (acc, [prop, fn]) => {
      if (Object.prototype.hasOwnProperty.call(obj, prop) && fn(obj[prop])) {
        return acc;
      }
      return false;
    },
    true
  );
};

// Validate content of booking dates object received from SessionStore
export const isValidBookingDates = (bookingDates) => {
  const props = {
    bookingStart: (d) => d instanceof Date,
    bookingEnd: (d) => d instanceof Date,
  };
  return validateProperties(bookingDates, props);
};

// Validate content of listing object received from SessionStore.
// Currently only id & attributes.price are needed.
export const isValidListing = (listing) => {
  const props = {
    id: (id) => id instanceof UUID,
    attributes: (v) => {
      return typeof v === 'object' && v.price instanceof Money;
    },
  };
  return validateProperties(listing, props);
};

// Validate content of an transaction received from SessionStore.
// An id is required and the last transition needs to be one of the known transitions
export const isValidTransaction = (transaction) => {
  const props = {
    id: (id) => id instanceof UUID,
    type: (type) => type === 'transaction',
    attributes: (v) => {
      return typeof v === 'object' && TRANSITIONS.includes(v.lastTransition);
    },
  };
  return validateProperties(transaction, props);
};

// Stores given bookingDates and listing to sessionStorage
export const storeData = (bookingData, bookingDates, listing, transaction, storageKey) => {
  if (typeof window !== 'undefined' && window.sessionStorage && listing && bookingDates && bookingData) {
    const data = {
      bookingData,
      bookingDates,
      listing,
      transaction,
      storedAt: new Date(),
    };

    const replacer = function (k, v) {
      if (this[k] instanceof Date) {
        return { date: v, _serializedType: 'SerializableDate' };
      }
      return sdkTypes.replacer(k, v);
    };

    const storableData = JSON.stringify(data, replacer);
    window.sessionStorage.setItem(storageKey, storableData);
  }
};

// bookingStart/bookingEnd don't always arrive as Date instances before they are
// stored (some checkout paths fill bookingDates with ISO strings), so storeData's
// replacer can't wrap them as SerializableDate and they round-trip as plain
// strings. Coerce them back to Date on read; otherwise isValidBookingDates rejects
// an otherwise-valid stored checkout and the page redirects to the listing,
// losing the in-progress booking (e.g. after returning from a pass subscription
// checkout). Genuinely unparseable values are returned as-is so validation still
// rejects them.
const toDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed;
  }
  return value;
};

// Get stored data
export const storedData = (storageKey) => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const checkoutPageData = window.sessionStorage.getItem(storageKey);

    const reviver = (k, v) => {
      if (v && typeof v === 'object' && v._serializedType === 'SerializableDate') {
        // Dates are expected to be stored as:
        // { date: new Date(), _serializedType: 'SerializableDate' }
        return new Date(v.date);
      }
      return sdkTypes.reviver(k, v);
    };

    const { bookingData, bookingDates, listing, transaction, storedAt } = checkoutPageData
      ? JSON.parse(checkoutPageData, reviver)
      : {};

    // Normalize booking dates to Date instances regardless of how they were
    // serialized (Date → SerializableDate, or a plain ISO string).
    const normalizedBookingDates =
      bookingDates && typeof bookingDates === 'object'
        ? {
            ...bookingDates,
            bookingStart: toDate(bookingDates.bookingStart),
            bookingEnd: toDate(bookingDates.bookingEnd),
          }
        : bookingDates;

    // If sessionStore contains freshly saved data (max 1 day old), use it
    const isFreshlySaved = storedAt
      ? moment(storedAt).isAfter(moment().subtract(1, 'days'))
      : false;

    // resolve transaction as valid if it is missing
    const isTransactionValid = transaction ? isValidTransaction(transaction) : true;

    const isStoredDataValid =
      isFreshlySaved &&
      isValidBookingDates(normalizedBookingDates) &&
      isValidListing(listing) &&
      isTransactionValid;

    if (isStoredDataValid) {
      return { bookingData, bookingDates: normalizedBookingDates, listing, transaction };
    }
  }
  return {};
};

// Persist the booking bootstrap so it survives a login round-trip.
//
// A logged-out user who requests a booking is bounced to the login page by the
// auth guard. Redux state usually survives that client-side round-trip, but a
// full page reload on the login page would wipe it. For logged-out users we
// therefore also write the booking data to sessionStorage, so CheckoutPage can
// restore it via storedData() once the user returns. Logged-in users never hit
// the login redirect, so there's nothing to persist (and writing would only
// risk staleness).
export const persistBookingForLogin = (currentUser, { listing, bookingData, bookingDates } = {}) => {
  if (currentUser) {
    return;
  }
  storeData(bookingData, bookingDates, listing, null, STORAGE_KEY);
};

export const clearData = (storageKey) => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem(storageKey);
  }
};
